<template>
  <v-menu
    v-model="isMenuOpen"
    offset-y
    content-class="mt-2"
    offset-overflow
    left
    :close-on-content-click="false"
  >
    <template #activator="{on, attrs, value}">
      <slot name="activator" :on="on" :attrs="attrs" :value="value">
        <button class="add-filter common-chip" v-bind="attrs" v-on="on">
          <div v-if="value" class="arrow-menu"></div>

          <div>{{ $t('add_filter') }}</div>
          <z-icon color="var(--black)" size="12">$add_rounded</z-icon>
        </button>
      </slot>
    </template>

    <z-card width="auto" max-width="450" style="padding: 16px">
      <template #content>
        <div class="d-flex" style="gap: 16px">
          <div style="width: 150px">
            <component
              :is="getAvailableComponent"
              v-model="internalSelectedParameter"
              class="mb-4"
              hide-details
              :label="$t('choose_parameter')"
              :items="parameters"
              @update:search-input="$emit('update:search-input', $event)"
              @change="$emit('update:parameter', $event)"
            ></component>

            <template v-if="internalSelectedParameter">
              <z-select
                v-model="internalSelectedOperation"
                class="mb-4 operation-select"
                small
                hide-details
                :label="$t('select_operation')"
                :items="currentOptions.operations"
              ></z-select>

              <z-select
                v-model="internalSelectedFilters"
                class="mb-4"
                multiple
                hide-details
                return-object
                truncate-values
                :label="$t('select')"
                :items="currentOptions.filters"
                @change="dispatchResizeEvent"
              ></z-select>
            </template>
          </div>

          <!-- Filtros selecionados na sidebar -->
          <template v-if="internalSelectedFilters.length">
            <v-divider
              class="mb-4"
              vertical
              style="border-color: var(--black-300)"
            ></v-divider>

            <div style="width: 200px">
              <div class="card-title mb-2">{{ $t('selected_filters') }}</div>

              <z-scrollbar style="max-height: 200px; padding-right: 10px">
                <div
                  v-for="(filter, index) of internalSelectedFilters"
                  :key="index"
                  class="selected-filter"
                >
                  <div class="infos">
                    <div>{{ infoCardText }}</div>

                    <div>
                      {{ filter.text }}
                    </div>
                  </div>

                  <z-icon
                    color="white"
                    size="20"
                    @click="onRemoveFilter(filter)"
                  >
                    $remove_circle_outline_rounded
                  </z-icon>
                </div>
              </z-scrollbar>
            </div>
          </template>
        </div>

        <v-divider
          style="border-color: var(--black-300)"
          class="mb-4"
        ></v-divider>

        <div class="d-flex align-center justify-end">
          <z-btn
            class="mr-4"
            :text="$t('cancel')"
            small
            rounded
            @click="isMenuOpen = false"
          >
          </z-btn>

          <z-btn
            small
            rounded
            primary
            :text="$t('add')"
            :disabled="
              !internalSelectedFilters.length || !internalSelectedOperation
            "
            @click="onAdd"
          ></z-btn>
        </div>
      </template>
    </z-card>
  </v-menu>
</template>

<script>
export default {
  props: {
    parameters: {
      type: Array,
      required: true,
      default: () => [],
    },

    parameterType: {
      type: String,
      required: false,
      default: 'select',
    },

    selectedParameter: {
      type: String,
      default: '',
    },

    selectedOperation: {
      type: String,
      default: '',
    },

    selectedFilters: {
      type: Array,
      default: () => [],
    },
  },

  i18n: {
    messages: {
      pt: {
        add_filter: 'Adicionar filtro',
        choose_parameter: 'Escolha o parâmetro',
        select_operation: 'Selecione uma operação',
        selected_filters: 'Filtros selecionados',
        select: 'Selecione',
        cancel: 'Cancelar',
        add: 'Adicionar',
      },
      en: {
        add_filter: 'Add filter',
        choose_parameter: 'Choose the parameter',
        select_operation: 'Select a operation',
        selected_filters: 'Selected filters',
        select: 'Select',
        cancel: 'Cancel',
        add: 'Add',
      },

      es: {
        add_filter: 'Agregar filtro',
        choose_parameter: 'Elegir el parámetro',
        select_operation: 'Seleccionar una operacion',
        selected_filters: 'Filtros seleccionados',
        select: 'Seleccionar',
        cancel: 'Cancelar',
        add: 'Agregar',
      },
      fr: {
        add_filter: 'Ajouter un filtre',
        choose_parameter: 'Choisir le paramètre',
        select_operation: 'Sélectionner une opération',
        selected_filters: 'Filtres sélectionnés',
        select: 'Sélectionner',
        cancel: 'Annuler',
        add: 'Ajouter',
      },
      tr: {
        add_filter: 'Filtre ekle',
        choose_parameter: 'Parametreyi seçin',
        select_operation: 'Bir işlem seçin',
        selected_filters: 'Seçilen filtreler',
        select: 'Seç',
        cancel: 'İptal',
        add: 'Ekle',
      },
    },
  },

  data() {
    return {
      isMenuOpen: false,

      internalSelectedParameter: '',
      internalSelectedOperation: '',
      internalSelectedFilters: [],
    }
  },

  computed: {
    infoCardText() {
      return this.parameters.find(
        (parameter) => parameter.value === this.internalSelectedParameter
      )?.text
    },

    currentOptions() {
      const operations =
        this.parameters.find(
          (parameter) => parameter.value === this.internalSelectedParameter
        )?.operations || []

      const filters =
        this.parameters.find(
          (parameter) => parameter.value === this.internalSelectedParameter
        )?.filters || []

      return {
        operations,
        filters,
      }
    },

    getAvailableComponent() {
      const availableComponents = {
        select: 'z-select',
        autocomplete: 'z-autocomplete',
      }

      return availableComponents[this.parameterType] || 'z-select'
    },
  },

  watch: {
    isMenuOpen() {
      //se alguma das props não foi definida, resetamos o estado interno no componente para que nada seja exibido
      if (
        !this.selectedParameter ||
        !this.selectedOperation ||
        !this.selectedFilters.length
      ) {
        this.$emit('update:menu')
        this.resetInternalState()
        return
      }

      //se o menu foi aberto/fechado, setamos o estado interno com as props
      this.internalSelectedParameter = this.selectedParameter
      this.internalSelectedOperation = this.selectedOperation
      this.internalSelectedFilters = this.selectedFilters
    },

    internalSelectedParameter(val) {
      this.dispatchResizeEvent()

      if (!val) {
        this.resetInternalState()
      }
    },

    selectedParameter: {
      immediate: true,
      handler(newVal) {
        this.internalSelectedParameter = newVal
      },
    },

    selectedFilters: {
      immediate: true,
      handler(newVal) {
        this.internalSelectedFilters = newVal
      },
    },

    selectedOperation: {
      immediate: true,
      handler(newVal) {
        this.internalSelectedOperation = newVal
      },
    },
  },

  mounted() {
    if (this.selectedParameter) {
      this.internalSelectedParameter = this.selectedParameter
    }
  },

  methods: {
    onAdd() {
      this.$emit('add:filters', {
        parameter: this.internalSelectedParameter,
        operation: this.internalSelectedOperation,
        filters: this.internalSelectedFilters,
      })

      if (
        !this.selectedParameter ||
        !this.selectedOperation ||
        !this.selectedFilters.length
      ) {
        this.resetInternalState()
      }

      this.isMenuOpen = false
    },

    onRemoveFilter(filter) {
      this.dispatchResizeEvent()

      this.internalSelectedFilters = this.internalSelectedFilters.filter(
        (selectedFilter) => selectedFilter.value !== filter.value
      )
    },

    resetInternalState() {
      this.internalSelectedParameter = ''
      this.internalSelectedOperation = ''
      this.internalSelectedFilters = []
    },

    dispatchResizeEvent() {
      this.$nextTick(() => {
        if (this.internalSelectedFilters.length === 0) {
          window.dispatchEvent(new Event('resize'))
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../common.scss' as *;

:deep(.zoox-select .v-select__selections) {
  padding: 0 !important;
}

.add-filter {
  background-color: transparent;
  border: 1px dashed var(--black);
}

.selected-filter {
  display: flex;
  align-items: center;
  background-color: var(--accent);
  justify-content: space-between;
  color: var(--white);
  padding: 4px 8px;
  border-radius: 10px;
  margin-bottom: 8px;

  .infos {
    display: flex;
    flex-direction: column;
    gap: 4px;

    & > :nth-child(1) {
      font-size: 11px;
      font-weight: 400;
      text-transform: uppercase;
    }

    & > :nth-child(2) {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
    }
  }
}

.arrow-menu {
  transform: translateX(-50%);
  position: absolute;
  border-bottom: solid 8px var(--white) !important;
  border-right: solid 8px transparent;
  border-left: solid 8px transparent;
  top: 21px;
  left: 50%;
  height: 0px;
  width: 0px;
  z-index: 9;
}
</style>
